import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { jwtDecode } from "jwt-decode";

import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import { User } from "@contexts/Auth";
import { urlRedirectCommissioningPortal } from "@settings";

import { useApiAuthPermissoesUsuario } from "@hooks/api/auth";
import { useApiStatusBloqueioCursoObrigatorio } from "@hooks/api/cursosObrigatorios";
import useAuth from "@hooks/useAuth";
import { useCorban } from "@hooks/useCorban";
import useFeatureFlags from "@hooks/useFeatureFlags";
import { PRODUCT_CODE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";

type PermissionsContext = {
  permissions: Record<string, string>;
  routes: Record<string, { canPermission: boolean; isBlocked?: boolean }>;
  allowedRoutes: string[];
  homeRoute: string;
  isReady: boolean;
  hasPermission: (permission: string) => boolean;
  refreshMandatoryCoursesBlock: () => Promise<
    User["mandatoryCoursesInfo"] | null
  >;
  loading: boolean;
};

export const PermissionsContext = createContext<PermissionsContext>({
  permissions: {},
  routes: {},
  allowedRoutes: [],
  homeRoute: "",
  isReady: false,
  hasPermission: () => false,
  refreshMandatoryCoursesBlock: () => Promise.resolve(null),
  loading: true,
});

export const PermissionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    fetchPermissoesUsuario,
    {
      data: fetchPermissionsData,
      loading: permissionsLoading,
      reset: resetFetchPermissions,
    },
  ] = useApiAuthPermissoesUsuario();
  const [fetchStatusBloqueio] = useApiStatusBloqueioCursoObrigatorio();

  const {
    user,
    updateUser,
    setProducts,
    hasProduct,
    hasProductBlocked,
    loading: userLoading,
  } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const { isReady: isReadyCorban } = useCorban();
  const isReady = !!fetchPermissionsData;

  const [permissions, setPermissions] = useState<
    PermissionsContext["permissions"]
  >({});
  const [loading, setLoading] = useState(true);
  const clearData = useCallback(() => {
    setPermissions({});
    resetFetchPermissions();
  }, [resetFetchPermissions]);

  const hasPermission = useCallback(
    (permission: string) => !!permissions[permission],
    [permissions],
  );

  const routes: PermissionsContext["routes"] = useMemo(() => {
    const BLOCK_PRIVATE_CONSIGNED = true;
    const blockedByMandatoryCourses =
      !!featureFlags.enableMandatoryCourses &&
      !!user?.mandatoryCoursesInfo?.bloqueado;

    return {
      "/servicos": { canPermission: true },

      "/roteiros-operacionais": {
        canPermission:
          hasPermission("add_contrato") &&
          !!featureFlags.enableOperationalGuide,
      },
      "/cursos/obrigatorios": {
        canPermission: !!featureFlags.enableMandatoryCourses,
      },
      "/comissionamento": {
        canPermission:
          hasPermission("acessar_comissionamento") &&
          !!urlRedirectCommissioningPortal,
      },

      "/cartao/compra-cliente": {
        canPermission:
          [
            PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
            PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
          ].some(hasProduct) && hasPermission("add_contrato"),
        isBlocked:
          ![
            PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
            PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
          ].some((code) => !hasProductBlocked(code)) ||
          blockedByMandatoryCourses,
      },
      "/cartao/cartao-beneficio": {
        canPermission:
          (hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) ||
            hasProduct(PRODUCT_CODE.CARTAO_CONSIGNADO)) &&
          hasPermission("add_contrato"),
        isBlocked:
          ![PRODUCT_CODE.CARTAO_BENEFICIO, PRODUCT_CODE.CARTAO_CONSIGNADO].some(
            (code) => !hasProductBlocked(code),
          ) || blockedByMandatoryCourses,
      },
      "/cartao/agregacao-margem": {
        canPermission:
          hasProduct(PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO) &&
          hasPermission("add_contrato"),
        isBlocked:
          blockedByMandatoryCourses ||
          hasProductBlocked(PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO),
      },
      "/saque-complementar": {
        canPermission:
          hasProduct(PRODUCT_CODE.CARTAO_BENEFICIO) &&
          hasPermission("add_contrato"),
        isBlocked:
          blockedByMandatoryCourses ||
          hasProductBlocked(PRODUCT_CODE.SAQUE_COMPLEMENTAR),
      },
      "/contratos": {
        canPermission: true,
        isBlocked: blockedByMandatoryCourses,
      },
      "/selecao-corban": { canPermission: true },
      "/termos-de-uso": { canPermission: true },
      "/politica-de-privacidade": { canPermission: true },

      "/notificacoes": { canPermission: true },

      "/sem-acesso": { canPermission: true },

      "/nao-me-perturbe": { canPermission: hasPermission("add_contrato") },

      // Submenu keys
      "/cartao": {
        canPermission: [
          PRODUCT_CODE.CARTAO_BENEFICIO,
          PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO,
          PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
          PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
        ].some(hasProduct),
        isBlocked: ![
          PRODUCT_CODE.CARTAO_BENEFICIO,
          PRODUCT_CODE.AGREGACAO_MARGEM_CARTAO,
          PRODUCT_CODE.COMPRA_CLIENTE_BENEFICIO,
          PRODUCT_CODE.COMPRA_CLIENTE_CONSIGNADO,
        ].some((code) => !hasProductBlocked(code)),
      },

      // CONSIGNADO PINE
      "/consignado": {
        canPermission: [
          PRODUCT_CODE.CONSIGNADO_PRIVADO,
          PRODUCT_CODE.CONSIGNADO_PUBLICO,
        ].some(hasProduct),
        isBlocked: ![
          PRODUCT_CODE.CONSIGNADO_PRIVADO,
          PRODUCT_CODE.CONSIGNADO_PUBLICO,
        ].some((code) => !hasProductBlocked(code)),
      },
      "/consignado/consignado-privado": {
        canPermission:
          hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
          hasPermission("add_contrato") &&
          !BLOCK_PRIVATE_CONSIGNED,
        isBlocked:
          blockedByMandatoryCourses ||
          hasProductBlocked(PRODUCT_CODE.CONSIGNADO_PRIVADO),
      },
      "/consignado/consignado-publico": {
        canPermission: hasProduct(PRODUCT_CODE.CONSIGNADO_PUBLICO),
        isBlocked:
          blockedByMandatoryCourses ||
          hasProductBlocked(PRODUCT_CODE.CONSIGNADO_PUBLICO),
      },

      "/consignado/acompanhamento-proposta": {
        canPermission:
          hasProduct(PRODUCT_CODE.CONSIGNADO_PRIVADO) &&
          hasPermission("add_contrato"),
        isBlocked: blockedByMandatoryCourses,
      },

      // USUARIOS
      "/menu-usuarios": {
        canPermission:
          (featureFlags.enableManageUsers &&
            ["can_listar_usuarios_corban"].some(hasPermission)) ||
          (!!user?.allowCreateUser &&
            [
              "happy_can_create_user",
              "happy_can_view_user",
              "happy_can_change_user",
            ].some(hasPermission)),
      },
      "/menu-favoritos": {
        canPermission: true,
      },
      "/usuarios": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
      "/usuarios/solicitacao": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
      "/usuarios/novo": {
        canPermission:
          !!user?.allowCreateUser && hasPermission("happy_can_create_user"),
      },
      "/usuarios/gerenciar": {
        canPermission:
          ["can_listar_usuarios_corban"].some(hasPermission) &&
          !!featureFlags.enableManageUsers,
      },

      // SEGUROS
      "/seguros": {
        canPermission: hasProduct(PRODUCT_CODE.SEGUROS),
        isBlocked: hasProductBlocked(PRODUCT_CODE.SEGUROS),
      },
      "/seguros/contratar": {
        canPermission:
          hasProduct(PRODUCT_CODE.SEGUROS) && hasPermission("add_contrato"),
        isBlocked:
          blockedByMandatoryCourses || hasProductBlocked(PRODUCT_CODE.SEGUROS),
      },
    } satisfies PermissionsContext["routes"];
  }, [user, hasProduct, hasPermission, hasProductBlocked, featureFlags]);

  const allowedRoutes = useMemo(() => {
    return Object.keys(routes).filter((route) => {
      const { canPermission, isBlocked = false } = routes[route];
      return canPermission && !isBlocked;
    });
  }, [routes]);

  const homeRoute = useMemo(() => allowedRoutes[0] || "", [allowedRoutes]);

  const refreshMandatoryCoursesBlock = useCallback(async () => {
    if (!featureFlags.enableMandatoryCourses) {
      return null;
    }

    try {
      const { data } = await fetchStatusBloqueio({
        shouldSkipErrorSnackbar: () => true,
      });
      const status = data ?? null;
      updateUser({ mandatoryCoursesInfo: status });
      return status;
    } catch {
      return null;
    }
  }, [featureFlags.enableMandatoryCourses, fetchStatusBloqueio, updateUser]);

  const fetchPermissions = useCallback(async () => {
    try {
      const [permissionsResult, blockResult] = await Promise.allSettled([
        fetchPermissoesUsuario(),
        fetchStatusBloqueio({
          shouldSkipErrorSnackbar: () => true,
        }),
      ]);

      if (permissionsResult.status === "rejected") {
        throw permissionsResult.reason;
      }

      const { data } = permissionsResult.value;

      const mandatoryCoursesInfo = (() => {
        if (blockResult.status === "fulfilled" && blockResult.value) {
          return blockResult.value.data ?? null;
        }
        return null;
      })();

      const userData: Partial<User> = {
        name: data.name,
        uniqueId: data.unique_id,
        identifier: data.identifier,
        enableFaceMatch: data.enable_face_match,
        isChecked: data.is_checked,
        checkUrl: data.check_url,
        supportChat: data.possui_chat_suporte,
        allowIlliterate: !!data.corban?.permite_analfabeto,
        allowLegalRepresentative: !!data.corban?.permite_representante,
        allowCardLegalRepresentative:
          !!data.corban?.permite_representante_cartao,
        allowCreateUser: !!data.corban?.permite_criar_usuario_originacao,
        id: data.id,
        mandatoryCoursesInfo,
      };

      updateUser(userData);

      const jwt = jwtDecode<{ permissions?: string[] }>(
        user?.accessToken ?? "",
      );
      const permissions = [
        ...new Set([...(jwt?.permissions ?? []), ...(data.permissions ?? [])]),
      ];
      setPermissions(
        permissions.reduce(
          (permissionsHashList, permission) => ({
            ...permissionsHashList,
            [permission]: true,
          }),
          {},
        ),
      );

      setProducts(
        data.produtos.map((product) => ({
          nome: product.nome,
          tipo_produto: getProductTypeFromProductCode(product.cd_produto),
          codigo_produto: product.cd_produto,
        })),
      );
    } catch (e) {
      if (
        !(e instanceof ApiInterceptorError) ||
        !axios.isCancel(e.sourceError)
      ) {
        clearData();
      }
    } finally {
      setLoading(false);
    }
  }, [
    fetchPermissoesUsuario,
    fetchStatusBloqueio,
    updateUser,
    setProducts,
    clearData,
    user?.accessToken,
  ]);

  useEffect(() => {
    if (!userLoading) {
      if (user?.refreshToken) {
        if (isReadyCorban) void fetchPermissions();
      } else {
        clearData();
        setLoading(false);
      }
    }
  }, [
    user?.refreshToken,
    userLoading,
    fetchPermissions,
    clearData,
    isReadyCorban,
  ]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        routes,
        allowedRoutes,
        homeRoute,
        isReady,
        hasPermission,
        refreshMandatoryCoursesBlock,
        loading: loading || permissionsLoading,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};
